// @ts-strict-ignore
import React, { useRef, useState } from 'react'
import { IconButton, Tooltip, Typography } from '@mui/material'
import TextArea from 'components/TextArea'
import ExternalLink from 'components/ExternalLink'
import FocusFadeGroup from 'components/FocusFadeGroup'
import { Create } from '@mui/icons-material'
import { FormValue } from 'types'
import RichTextDescription from '../../RichTextDescription'
import FormErrorHint from '../../FormErrorHint'
import useStyles from './styles'

interface ICompanyDescriptionFieldProps {
  companyDescription: FormValue<string>
  editable: boolean
}

export default function CompanyDescriptionField(props: ICompanyDescriptionFieldProps) {
  const { classes } = useStyles()
  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false)

  const { companyDescription: { input, meta }, editable } = props

  const previewWrapperRef = useRef<HTMLDivElement>(null)
  const previewWrapperHeightRef = useRef<number>(0)

  const closeEditor = () => {
    setIsEditorOpen(false)
  }

  const handleTextAreaBlur = (e) => {
    input.onBlur(e)

    // to make the "markdown" helper link clickable, the hide-on-blur mechanism has to be put on the end of the event loop
    window.setTimeout(() => {
      closeEditor()
    }, 0)
  }

  const handleDialogOpenToggle = () => {
    if (isEditorOpen) {
      closeEditor()
    } else {
      if (previewWrapperRef.current) {
        previewWrapperHeightRef.current = previewWrapperRef.current.clientHeight
      }

      setIsEditorOpen(true)
    }
  }

  if (!input.value && !editable) {
    return null
  }

  return (
    <FocusFadeGroup focused={isEditorOpen}>
      <div className={classes.container}>
        {!isEditorOpen && (
          <div ref={previewWrapperRef}>
            <RichTextDescription
              style={{ display: 'inline-block', textAlign: 'justify', maxWidth: editable ? 'calc(100% - 72px)' : '100%', boxSizing: 'border-box' }}
              text={input.value || 'About the company...'}
              contentStyle={input.value ? undefined : { opacity: 0.6 }}
            />

            {editable && (
              <Tooltip title="Edit this section" placement="right">
                <IconButton onClick={handleDialogOpenToggle} className={classes.iconButton} data-cy={`job-${input.name}-open`}>
                  <Create />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}

        {isEditorOpen && (
          <div className={classes.editorWrapper}>
            <TextArea
              autoFocus
              input={{ ...input, onBlur: handleTextAreaBlur }}
              meta={meta}
              label="About the company"
              placeholder="About the company..."
              style={{
                width: 9999,
                maxWidth: '100%',
                minHeight: 180,
                height: previewWrapperHeightRef.current - 31 || 'auto',
              }}
              data-cy={`job-${input.name}-input`}
            />

            <Typography variant="body1" className={classes.markdownHint}>
              You can use <ExternalLink href="https://en.wikipedia.org/wiki/Markdown#Example" label="markdown" showExternalIcon mouseDown /> to format contents.
            </Typography>
          </div>
        )}

        {(!isEditorOpen) && (
          <FormErrorHint message={meta.touched && meta.error} />
        )}
      </div>
    </FocusFadeGroup>
  )
}
